'use client';
import { useState, useEffect } from 'react';
import { checkShippingAction, reverseGeocodeAction, fetchDeliveryCheckAction, saveDeliveryCheckAction } from '@/lib/server/actions';

export const DELIVERY_TIMELINE_NOTE = "We'll do our best to deliver earlier. This is the maximum expected timeline.";

export const formatDeliveryDate = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + Number(days));
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

// "X day(s)" for known EDD; "5–6 business days" when courier (e.g. Ekart) returns null.
export const formatDaysLabel = (days) =>
    days != null ? `${days} day${days !== 1 ? 's' : ''}` : '5–6 business days';

export const useShipping = () => {
    const [pincode, setPincode] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [detectingLocation, setDetectingLocation] = useState(false);
    // True until the initial fetch of the account's last saved delivery check
    // has resolved (found or not) — lets consumers avoid racing their own
    // "auto-check the default address" fallback against this fetch.
    const [initializing, setInitializing] = useState(true);

    useEffect(() => {
        let cancelled = false;
        fetchDeliveryCheckAction()
            .then((json) => {
                if (cancelled || !json?.success || !json.data?.pincode) return;
                setResult(json.data);
                setPincode(String(json.data.pincode));
            })
            .finally(() => { if (!cancelled) setInitializing(false); });
        return () => { cancelled = true; };
    }, []);

    const checkPincode = async (pin) => {
        const trimmed = String(pin || pincode).trim();
        if (!/^\d{6}$/.test(trimmed)) {
            setError('Enter a valid 6-digit pincode');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const json = await checkShippingAction(trimmed);
            if (json.success) {
                const raw = json.data.estimatedDays;
                const days = raw != null ? Number(raw) : null;
                const adjusted = {
                    ...json.data,
                    estimatedDays: days,
                    deliveryDate: days != null ? formatDeliveryDate(days) : null,
                };
                setResult(adjusted);
                setPincode(String(adjusted.pincode));
                // Synced to the account server-side (silently a no-op for guests).
                saveDeliveryCheckAction(adjusted).catch(() => {});
            } else {
                setError(json.message || 'Unable to check pincode');
                setResult(null);
            }
        } catch {
            setError('Something went wrong. Try again.');
        } finally {
            setLoading(false);
        }
    };

    const clear = () => {
        setResult(null);
        setPincode('');
        setError(null);
    };

    // "Use my current location" — browser geolocation + reverse geocode,
    // then auto-runs the delivery check with the detected pincode.
    const useCurrentLocation = () => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            setError('Location access is not supported by your browser.');
            return;
        }
        setError(null);
        setDetectingLocation(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                const res = await reverseGeocodeAction(latitude, longitude);
                setDetectingLocation(false);
                if (res?.success && res.data?.pincode) {
                    setPincode(res.data.pincode);
                    checkPincode(res.data.pincode);
                } else {
                    setError(res?.message || 'Could not detect your pincode. Please enter it manually.');
                }
            },
            (err) => {
                setDetectingLocation(false);
                setError(
                    err.code === err.PERMISSION_DENIED
                        ? 'Location permission denied. Please enable it in your browser settings.'
                        : 'Could not detect your location. Please enter it manually.'
                );
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    return {
        pincode, setPincode, result, loading, error, setError, checkPincode, clear,
        useCurrentLocation, detectingLocation, initializing,
    };
};
